import { LinkTo } from '@ember/routing';
import { PhLightbulbFilament, PhConfetti, PhGear, PhSiren } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { VStack } from 'tio-ui/components/layout';
import Component from '@glimmer/component';
import Tile from 'tio-employee/components/dashboard/tile';
import type SessionContextService from 'tio-employee/services/session-context';

interface ComponentDefinition {
  name: string;
  sub_title?: string;
  subtitle?: string;
  text: string;
  type: string;
  order?: number;
}

interface WellnessData {
  components?: ComponentDefinition[];
  obs_updated_at?: string;
  obsUpdatedAt?: string;
  [key: string]: unknown;
}

interface WellnessInsightsSignature {
  Args: {
    wellnessData?: WellnessData | null;
  };
}

export default class DashboardWidgetsWellnessInsightsComponent extends Component<WellnessInsightsSignature> {
  @service declare sessionContext: SessionContextService;

  get wellnessTrain() {
    return this.args.wellnessData;
  }

  get components(): ComponentDefinition[] {
    // Raw API returns 'components', serialized model has 'componentDefinitions'
    return this.wellnessTrain?.components || [];
  }

  get scoreDefinition(): ComponentDefinition | undefined {
    return this.components.find((c) => c.type === 'score');
  }

  get recommendationDefinitions(): ComponentDefinition[] {
    const recommendationTypes = [
      'to_coaching',
      'to_pslf_tool',
      'to_idr_tool',
      'to_nslds_upload',
      'to_refinancing_tool',
      'to_strategy_finder',
      'to_pslf_form_submission',
      'transitional_strategy_finder_results',
      'transitional_pslf_form_submission',
      'transitional_pslf_tool_results',
      'transitional_idr_results',
    ];
    return this.components
      .filter((c) => recommendationTypes.includes(c.type))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  get hasScore() {
    return !!this.scoreDefinition;
  }

  get scoreIntent(): string {
    switch (this.scoreDefinition?.name) {
      case 'score_on_track':
        return 'tio-fg-success';
      case 'score_need_adjustment':
        return 'tio-fg-warning';
      case 'score_need_attention':
        return 'tio-fg-danger';
      default:
        return '';
    }
  }

  get asOfDate(): string {
    // Raw API uses snake_case, serialized model uses camelCase
    const dateString = this.wellnessTrain?.obs_updated_at || this.wellnessTrain?.obsUpdatedAt;
    if (!dateString) return '';
    return new Date(dateString as string).toLocaleDateString('en-US');
  }

  <template>
    <LinkTo @route="authenticated.wellness-insights">
      <Tile @headerText={{t "wellness_insights.student_loan_insights.title"}}>
        <:body>
          {{#if this.hasScore}}
            <VStack class="gap-8">
              {{#let this.scoreDefinition.name as |scoreType|}}
                <VStack class="tio-stack-center gap-0">
                  <div class="tio-row-center font-semibold gap-2 {{this.scoreIntent}}">
                    {{t "wellness_insights.dashboard.components.score.label" (hash name=scoreType)}}
                    {{#if (eq scoreType "score_on_track")}}
                      <PhConfetti @size={{24}} />
                    {{else if (eq scoreType "score_need_adjustment")}}
                      <PhGear @size={{24}} />
                    {{else if (eq scoreType "score_need_attention")}}
                      <PhSiren @size={{24}} />
                    {{/if}}
                  </div>
                  <p>
                    {{t
                      "wellness_insights.dashboard.components.score.as_of_date"
                      (hash date=this.asOfDate)
                    }}
                  </p>
                </VStack>
              {{/let}}
              <div class="tio-col-tight">
                <h4 class="tio-text-base-semibold">
                  {{t "wellness_insights.dashboard.recommendations_title"}}
                </h4>
                {{#each this.recommendationDefinitions as |recommendation|}}
                  <p>{{recommendation.sub_title}}</p>
                {{/each}}
              </div>
            </VStack>
          {{else}}
            <VStack>
              <PhLightbulbFilament @size={{108}} class="block mx-auto" />
              <h4 class="w-fit mx-auto tio-text-base-semibold">
                {{t "wellness_insights.dashboard.components.placeholder.title"}}
              </h4>
              <p class="text-center">
                {{t "wellness_insights.dashboard.components.placeholder.body"}}
              </p>
            </VStack>
          {{/if}}
        </:body>
      </Tile>
    </LinkTo>
  </template>
}
